import React from 'react';
import { Alert, Box, useMediaQuery, useTheme } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import DeviceCompletionNotification from './DeviceCompletionNotification';
import PreviewBanner from './PreviewBanner';
import PageSkeleton from './PageSkeleton';
import AssistantChatModal from './AssistantChatModal';
import { useEnvironment, useFeature } from '../EnvironmentContext';
import { useAuth } from '../AuthContext';
import { useTeamAccess } from '../TeamAccessContext';
import { isProfileRouteAllowed } from '../configurationProfile';
import { allAdminTools } from './toolCatalog';
import { visibleTiles } from './ToolTileGrid';
import { layout } from '../ui-theme';
import { isAccessibilityAuditSession } from '../accessibilityAudit';

const STORAGE_KEY_SIDEBAR = 'askiam-sidebar-open';

const pageTitles: Record<string, string> = {
  '/': 'Home',
  '/my-requests': 'My Requests',
  '/app-consent': 'Admin Consent - Production Tenants',
  '/manage-requests': 'Manage Requests',
  '/manage-exceptions': 'Manage Exceptions',
  '/bulk-upload-exceptions': 'Bulk Upload Exceptions',
  '/exception-report': 'Exception Report',
  '/requests': 'Request Central',
  '/exceptions': 'Exception Central',
  '/attestations': 'Attestation Central',
  '/attestations/uami': 'UAMI Attestation',
  '/attestations/uami/admin': 'UAMI Campaign Administration',
  '/attestations/custom-roles': 'Custom Role Attestation',
  '/attestations/app': 'Application Attestation',
  '/claims': 'My Claims',
  '/clearance-evaluate': 'Clearance Evaluate',
  '/harvest': 'Harvest Management',
  '/onboarding-buddy': 'Onboarding Buddy',
  '/work-items/onboard': 'Onboard Team',
  '/work-items/routing': 'Routing Administration',
  '/work-items/deflection-catalog': 'Deflection Catalog',
  '/work-items/taxonomy-mapping': 'Deflection Taxonomy Mapping',
  '/work-items/teams': 'Manage Team',
  '/work-requests/new': 'Request Engineering Work',
  '/work-requests/triage': 'Team Backlog',
  '/guestpass': 'Guest Pass',
  '/hr-account-lookup': 'HR Account Lookup',
  '/coreid-hr-account-lookup': 'CoreID HR Lookup',
  '/hr-account-lookup/faq': 'HR Account Lookup — FAQ',
  '/hr-account-lookup/logs/all': 'HR Account Lookup — All Logs',
  '/guestpass-old': 'Guest Pass (Classic UI)',
  '/user-tools': 'User Tools',
  '/admin-tools': 'Admin Tools',
  '/reports': 'Reports',
  '/management-tools': 'Management Tools',
  '/boards/bug': 'Bug Board',
  '/boards/idea': 'Idea Board',
  '/whoami': 'WhoAmI',
  '/inactive-users': 'Inactive Users Report',
  '/ipanchor': 'IP Anchor',
  '/ipanchor/admin': 'IP Anchor Admin',
  '/icm-report': 'ICM Incidents Report',
  '/reports/intake': 'Intake Reporting',
  '/reports/kr-counts': 'KR Reporting',
  '/reports/account-activity': 'Account Activity Report',
  '/reports/fed-snow': 'FedServiceNow Open Incidents',
  '/reports/pain-meter': 'JMM Pain Meter',
  '/reports/relief-effectiveness': 'Relief Effectiveness',
  '/reports/triage-aging': 'Triage Aging Report',
  '/reports/overview': 'Report Central Overview',
  '/reports/intake-funnel': 'Intake Funnel Report',
  '/reports/hot-spots': 'Hot Spots Report',
  '/reports/cross-surface-hot-spots': 'Cross-Surface Hot Spots Report',
  '/reports/triage-lag': 'Triage Lag Trend Report',
  '/reports/triage-timing': 'Triage Timing Report',
  '/reports/template-adoption': 'Template Adoption Report',
  '/reports/outcome-mix': 'Outcome Mix Report',
  '/reports/approval-performance': 'Approval Performance Report',
  '/reports/rejection-reasons': 'Rejection Reasons Report',
  '/reports/workflow-health': 'Workflow Failure Health Report',
  '/reports/exception-expiry': 'Exception Expiry Report',
  '/reports/data-quality': 'Reporting Data Quality',
  '/reports/routing': 'Routing Accuracy Report',
  '/reports/declined-detail': 'Denied and Redirected Detail',
  '/reports/deflection': 'Deflection Effectiveness Report',
  '/reports/elevated-access': 'Elevated Access Insights',
  '/analytics': 'Analytics',
  '/pim-sync': 'PIM Sync',
  '/contributors': 'Contributors',
  '/clearance': 'Clearance',
  '/clearance/flow': 'Clearance Flow',
  '/clearance/applications': 'Clearance Applications',
  '/clearance/operation-contexts': 'Clearance Contexts',
  '/clearance/services': 'Clearance Services',
  '/clearance/tenants': 'Clearance Tenants',
  '/clearance/evaluate': 'Clearance Evaluate',
  '/clearance/visualization': 'Clearance Visualization',
  '/helpdesk': 'Helpdesk',
  '/helpdesk/cafe': 'Helpdesk CAFE',
  '/helpdesk/risk': 'Helpdesk RISK',
  '/helpdesk/falcon': 'Helpdesk Falcon',
  '/helpdesk/phish': 'Helpdesk Phish',
  '/iam-agent-assistant': 'IAM Agent Assistant',
  '/helpdesk/iam-agent-assistant': 'IAM Agent Assistant',
  '/admin/diagnostics': 'Diagnostics',
  '/admin/operations-center': 'Operations Center',
  '/admin/operations-center/health': 'Operations Center',
  '/change-radar': 'Change Impact Radar',
  '/change-radar/dashboard': 'Change Radar Dashboard',
  '/change-radar/baselines': 'Change Radar Baselines',
  '/change-radar/monitoring': 'Change Radar Monitoring',
  '/change-radar/go-no-go': 'Change Radar Go/No-Go',
  '/conditional-access-readiness': 'Conditional Access Readiness',
};

const Layout: React.FC = () => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SIDEBAR);
    return saved === null ? true : saved === 'true';
  });
  const location = useLocation();
  React.useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, mobile]);
  const { configurationProfile, flagsReady, flags, features } = useEnvironment();
  const { user } = useAuth();
  const auditReadOnly = isAccessibilityAuditSession(user);
  const teamAccess = useTeamAccess();
  const restricted = configurationProfile === 'IAMPProdOnly';
  const assistantFeature = useFeature('Assistant.Guidance');
  const assistantEnabled = !restricted && assistantFeature;
  // Routes match case-insensitively (/ipanchor and /IPAnchor are the same page), so titles do too.
  const title = pageTitles[location.pathname] || pageTitles[location.pathname.toLowerCase()]
    || (location.pathname.startsWith('/reports/category/') ? 'Reports'
    : location.pathname.startsWith('/requests/new/') ? 'New Request'
    : location.pathname.startsWith('/work-requests/') ? 'Engineering Work Request'
    : location.pathname.startsWith('/requests/') ? 'Request Detail'
    : location.pathname.toLowerCase().startsWith('/ipanchor/accounts/') ? 'IP Anchor Account'
    : 'AskIAM');

  React.useEffect(() => {
    document.title = `${title} | AskIAM`;
  }, [title]);

  const handleToggleSidebar = () => {
    if (mobile) {
      setMobileSidebarOpen(prev => !prev);
      return;
    }
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY_SIDEBAR, String(next));
      return next;
    });
  };

  if (restricted && !flagsReady) return <PageSkeleton />;
  const eligibleAdminPaths = visibleTiles(allAdminTools, { roles: user?.roles ?? [], flags, features, teamAccess })
    .map((tile) => tile.path);
  // Decide before rendering Outlet: excluded pages must not mount or issue requests.
  if (!isProfileRouteAllowed(configurationProfile, location.pathname, eligibleAdminPaths)
      || (restricted && location.pathname === '/')) {
    return <Navigate to="/requests" replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header
        title={title}
        onToggleSidebar={handleToggleSidebar}
        sidebarOpen={mobile ? mobileSidebarOpen : sidebarOpen}
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Sidebar
          collapsed={!mobile && !sidebarOpen}
          mobile={mobile}
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'margin-left 0.2s',
            // Without an explicit background here, every element inside main is
            // transparent all the way up to <body>. A contrast checker then has
            // to keep walking ancestors to find something to measure against,
            // and anything it meets on the way - a gradient, an overlay - makes
            // the whole subtree unmeasurable. Naming the colour that is already
            // being shown costs nothing and makes the page answerable.
            bgcolor: 'background.default',
          }}
        >
          {/*
            The banner sits outside the Suspense boundary so an active preview stays visible while
            a page chunk loads, rather than disappearing behind the skeleton.
          */}
          <PreviewBanner />
          {auditReadOnly && (
            <Alert severity="info" role="status" sx={{ borderRadius: 0 }}>
              Accessibility audit access is read-only except for Report a Bug. Other business changes are blocked.
            </Alert>
          )}
          {/*
            The Suspense boundary sits here, inside the layout, so a page chunk loading leaves the
            header and sidebar on screen. Placing it above Layout would blank the whole shell on
            every first navigation to a route.
          */}
          <React.Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </React.Suspense>
        </Box>
      </Box>
      {assistantEnabled && <AssistantChatModal />}
      {!restricted && <DeviceCompletionNotification />}
    </Box>
  );
};

export default Layout;
